import { useState, useCallback, useMemo, useEffect } from 'react';
import { toast } from 'react-toastify';
import * as yup from 'yup';
import ServiceCRUD from '../services/ServiceNPS';

const tagService = new ServiceCRUD('/tags');

export const tagSchema = yup.object().shape({
  _id: yup.string().nullable(),
  name: yup.string().required('O nome da tag é obrigatório'),
  active: yup.boolean().default(true)
});

const formResolverSchema = tagSchema;

const useSurveyTags = () => {
  const [state, setState] = useState({
    tags: [],
    allTags: [],
    totalRegisters: 0,
    activePage: 1,
    isLoading: false,
    isError: false,
    error: null
  });

  const setLoading = (loading = true) =>
    setState((prev) => ({
      ...prev,
      isLoading: loading,
      isError: false,
      error: null
    }));

  const setError = (err) =>
    setState((prev) => ({
      ...prev,
      isLoading: false,
      isError: true,
      error: err
    }));

  const ensureArray = (data) => (Array.isArray(data) ? data : []);

  /**
   * Busca todas as tags SEM paginação
   * Endpoint esperado: /list/enabled (retornando { list: [...] } ou array direto)
   */
  const getAllTags = useCallback(async () => {
    setLoading(true);
    try {
      const response = await tagService.showTags();

      const rawData = response.data?.list || response.data || [];
      const data = ensureArray(rawData);

      setState((prev) => ({
        ...prev,
        isLoading: false,
        allTags: data
      }));

      return data;
    } catch (err) {
      console.error('Erro ao buscar lista completa de tags:', err);
      toast.error('Não foi possível carregar a lista de tags.', {
        autoClose: 3000
      });
      setError(err);
      setState((prev) => ({ ...prev, allTags: [], isLoading: false }));
      return [];
    }
  }, []);

  /**
   * Busca tags COM paginação
   * Estrutura de resposta esperada: { list: [...], count: 4 }
   */
  const fetchAllTags = useCallback(
    async (page = state.activePage, querySearch = '', showDisable = false) => {
      setLoading(true);
      try {
        const response = await tagService.getAll(
          page,
          querySearch,
          showDisable
        );

        const rawData = response.data?.list || [];
        const data = ensureArray(rawData);

        const total = response.data?.count || data.length || 0;

        setState((prev) => ({
          ...prev,
          isLoading: false,
          tags: data,
          totalRegisters: total,
          activePage: page
        }));

        return data;
      } catch (err) {
        console.error('Erro ao listar tags paginadas:', err);
        toast.error('Não foi possível carregar a lista de tags.', {
          autoClose: 3000
        });
        setError(err);
        setState((prev) => ({ ...prev, tags: [], isLoading: false }));
        return [];
      }
    },
    [state.activePage]
  );

  const getTagById = useCallback(async (id) => {
    setLoading(true);
    try {
      const response = await tagService.get(id);
      const data = response?.data;
      setLoading(false);
      return data;
    } catch (err) {
      console.error('Erro ao buscar tag por id:', err);
      toast.error('Erro ao carregar detalhes da tag.', { autoClose: 3000 });
      setError(err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const createTag = useCallback(
    async (data) => {
      setLoading(true);
      try {
        const response = await tagService.create(data);
        toast.success(`Tag criada: ${response.data?.msg || 'OK'}`, {
          autoClose: 3000
        });

        await fetchAllTags(1);
        getAllTags();

        return response.data;
      } catch (err) {
        console.error('Erro ao criar tag:', err);
        const msg = err.response?.data?.msg || 'Não foi possível criar a tag.';
        toast.error(msg, { autoClose: 3000 });
        setError(err);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [fetchAllTags, getAllTags]
  );

  const updateTag = useCallback(
    async (id, data) => {
      setLoading(true);
      try {
        if (!id) throw new Error('ID obrigatório');
        const response = await tagService.update(id, data);
        toast.info(`Tag atualizada: ${response.data?.msg || 'Sucesso'}`, {
          autoClose: 3000
        });

        await fetchAllTags(state.activePage);
        getAllTags();

        return response.data;
      } catch (err) {
        console.error('Erro ao atualizar tag:', err);
        toast.error(err.response?.data?.msg || 'Erro ao atualizar tag.', {
          autoClose: 3000
        });
        setError(err);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [fetchAllTags, getAllTags, state.activePage]
  );

  const toggleTagStatus = useCallback(
    async (id, currentStatus) => {
      try {
        const payload = { active: !currentStatus };
        const response = await tagService.enableConfig(id, payload);

        if (response) {
          toast.success('Status da tag alterado com sucesso');
          await fetchAllTags(state.activePage);
          getAllTags();
        }
        return response.data;
      } catch (err) {
        console.error('Erro ao alternar status:', err);
        toast.error('Erro ao alterar status da tag', { autoClose: 3000 });
        throw err;
      }
    },
    [fetchAllTags, getAllTags, state.activePage]
  );

  const deleteTag = useCallback(
    async (id) => {
      setLoading(true);
      try {
        await tagService.delete(id);
        toast.success('Tag removida com sucesso');
        await fetchAllTags(state.activePage);
        getAllTags();
      } catch (err) {
        console.error('Erro ao deletar tag:', err);
        toast.error('Erro ao remover a tag.', { autoClose: 3000 });
        setError(err);
      } finally {
        setLoading(false);
      }
    },
    [fetchAllTags, getAllTags, state.activePage]
  );

  const options = useMemo(() => {
    const sourceList =
      state.allTags && state.allTags.length > 0 ? state.allTags : state.tags;

    if (!Array.isArray(sourceList)) {
      return [];
    }

    return sourceList.map((item) => ({
      label: item.name,
      value: item._id
    }));
  }, [state.tags, state.allTags]);

  useEffect(() => {
    fetchAllTags();
  }, []);

  const { tags, allTags, ...restState } = state;

  return {
    ...restState,
    response: {
      paginated: Array.isArray(tags) ? tags : [],
      all: Array.isArray(allTags) ? allTags : []
    },
    fetchAllTags,
    getAllTags,
    getTagById,
    createTag,
    updateTag,
    deleteTag,
    toggleTagStatus,
    options,
    schemas: {
      tagSchema,
      formResolverSchema
    }
  };
};

export default useSurveyTags;
